/**
 * The Quality tab strip — NCRs, Concessions, Approval Register and (CR-7352)
 * My Collections.
 *
 * Each Quality page used to carry its own copy of this markup, which is how
 * the strip drifted between pages in the first place. One component, one
 * `active` prop, so a tab added here appears everywhere it should.
 *
 * @param active - which page is being rendered, so its tab is highlighted
 * @param tabVisibility - the platform-settings toggles the page already fetched
 * @param concessionsEnabled - the tenant's concessions feature switch; the
 *   Concessions tab needs BOTH this and the visibility toggle, or it links to
 *   a page that bounces straight back to /quality
 * @param isCustomer - My Collections is customer-side only; suppliers are
 *   never assignees
 * @param myCollectionsCount - open collections waiting on this user, shown in
 *   the tab label; 0 shows no number
 */
import { Link } from 'react-router-dom';

import type { QualityTabsVisibility } from '../../lib/api';

export type QualityTab = 'ncrs' | 'concessions' | 'approval_register' | 'my_collections';

const BASE = 'px-4 py-2 text-sm font-medium rounded-md shadow-sm transition-colors';
const IDLE = `${BASE} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400`;
const ACTIVE = `${BASE} border-2 border-primary bg-primary text-primary-foreground`;

interface QualityTabStripProps {
  active: QualityTab;
  tabVisibility: QualityTabsVisibility;
  concessionsEnabled: boolean;
  isCustomer: boolean;
  myCollectionsCount?: number;
}

export function QualityTabStrip({
  active, tabVisibility, concessionsEnabled, isCustomer, myCollectionsCount = 0,
}: QualityTabStripProps) {
  const cls = (tab: QualityTab) => (tab === active ? ACTIVE : IDLE);
  return (
    <nav className="flex space-x-2">
      {tabVisibility.ncrs && (
        <Link to="/quality" className={cls('ncrs')}>NCRs</Link>
      )}
      {tabVisibility.concessions && concessionsEnabled && (
        <Link to="/concessions" className={cls('concessions')}>
          Production Permit / Concession
        </Link>
      )}
      {tabVisibility.approval_register && (
        <Link to="/quality/approval-register" className={cls('approval_register')}>
          Approval Register
        </Link>
      )}
      {isCustomer && (
        <Link to="/quality/my-collections" className={cls('my_collections')}>
          My Collections{myCollectionsCount > 0 ? ` (${myCollectionsCount})` : ''}
        </Link>
      )}
    </nav>
  );
}

export default QualityTabStrip;
